import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated, getAuthHeaders } from '../utils/auth';
import { fetchCreditsCached } from '../services/credits';
import { API_ENDPOINTS } from '../config/api';
import DesktopAppDownload from '../components/DesktopAppDownload';
import './Profile.css';

function Profile() {
  const navigate = useNavigate();
  const [credits, setCredits] = useState(null);
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [loadingReferral, setLoadingReferral] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // CRITICAL: Redirect to signup if not authenticated
  // Uses deferred check to handle production hydration timing
  useEffect(() => {
    const checkAuth = () => {
      if (!isAuthenticated()) {
        // Recheck after brief delay for production builds
        setTimeout(() => {
          if (!isAuthenticated()) {
            navigate('/signup');
          } else {
            setAuthChecked(true);
          }
        }, 100);
      } else {
        setAuthChecked(true);
      }
    };
    checkAuth();
  }, [navigate]);
  
  // Get user email from localStorage
  const email = localStorage.getItem('userEmail') || 
                localStorage.getItem('bk_email') || 
                'user@example.com';

  const handleSignOut = () => {
    // Use centralized logout to clear cookies + localStorage
    const { logout } = require('../utils/auth');
    logout();
    
    // Redirect to signup page
    navigate('/signup');
  };

  const handleBuyCredits = () => {
    navigate('/buy-credits');
  };

  useEffect(() => {
    const fetchCredits = async () => {
      setLoadingCredits(true);
      try {
        const data = await fetchCreditsCached();
        setCredits(data.totalCredits || 0);
      } catch {
        setCredits(0);
      } finally {
        setLoadingCredits(false);
      }
    };

    fetchCredits();
  }, []);

  // Fetch user's referral code
  useEffect(() => {
    const fetchReferralCode = async () => {
      setLoadingReferral(true);
      try {
        const res = await fetch(API_ENDPOINTS.REFERRALS.MY_CODE, {
          method: 'GET',
          credentials: 'include',
          headers: getAuthHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          setReferralCode(data.referralCode || '');
        }
      } catch (e) {
        console.error('Failed to fetch referral code:', e);
      } finally {
        setLoadingReferral(false);
      }
    };
    if (authChecked) fetchReferralCode();
  }, [authChecked]);

  const referralLink = referralCode
    ? `${window.location.origin}/buy-credits?ref=${referralCode}`
    : '';

  const handleCopyReferral = useCallback(async () => {
    if (!referralCode) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = referralLink;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [referralCode, referralLink]);

  const shareText = `Practice mock interviews with AI on BlackKiteAI! Use my referral code: ${referralCode}`;
  const encodedShareText = encodeURIComponent(shareText);
  const encodedLink = encodeURIComponent(referralLink);

  return (
    <div className="profile-page">
      <div className="profile-container">
        <div className="profile-header">
          <h1>Profile</h1>
          <button className="btn-sign-out" onClick={handleSignOut}>
            Sign Out
          </button>
        </div>

        <div className="profile-content">
          <div className="profile-card">
            <h2 className="profile-card-title">Email</h2>
            <p className="profile-email">{email}</p>
          </div>

          <div className="profile-card">
            <h2 className="profile-card-title">Interview Credits</h2>
            <p className="profile-credits">{loadingCredits ? '...' : credits ?? 0}</p>
            <button className="btn-buy-credits" onClick={handleBuyCredits}>
              Buy Interview Credits
            </button>
          </div>

          <DesktopAppDownload />

          {/* Referral Code Card */}
          <div className="profile-card referral-card">
            <h2 className="profile-card-title">Your Referral Code</h2>
            {loadingReferral ? (
              <p style={{ color: '#9fb1c8' }}>Loading...</p>
            ) : referralCode ? (
              <>
                <div className="referral-code-display">
                  <span className="referral-code-text">{referralCode}</span>
                  <button className="btn-copy-referral" onClick={handleCopyReferral}>
                    {copied ? '✓ Copied!' : '📋 Copy Link'}
                  </button>
                </div>
                <p className="referral-link-text">{referralLink}</p>
                <div className="referral-share-buttons">
                  <a
                    href={`https://wa.me/?text=${encodedShareText}%20${encodedLink}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-btn share-whatsapp"
                    title="Share on WhatsApp"
                  >
                    WhatsApp
                  </a>
                </div>
                <button className="btn-referral-history" onClick={() => navigate('/referral-history')}>
                  View Referral History
                </button>
              </>
            ) : (
              <p style={{ color: '#9fb1c8' }}>No referral code available yet. Complete a purchase to get one!</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;
